<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLiveStore } from '@/stores/live'
import { PresetRegistry } from '@/presets/registry'
import { isDetectedPreset, type DetectedPreset } from '@/core/preset-selection'

const props = defineProps<{ startupOccupied: boolean }>()
const emit = defineEmits<{ 'visibility-change': [occupied: boolean] }>()
const { t } = useI18n()
const live = useLiveStore()
const selected = ref<DetectedPreset | null>(null)
const presets = PresetRegistry.list()
const multiple = computed(() => live.gameTypeChoice?.reason === 'ambiguous')
const visible = computed(() => live.gameTypeChoiceOpen && !props.startupOccupied)
watch(() => live.gameTypeChoice, choice => {
  if (choice) selected.value = live.profile && isDetectedPreset(live.profile.presetId) ? live.profile.presetId : null
}, { immediate: true })
watch(visible, open => { if (open) emit('visibility-change', true) }, { immediate: true })
onBeforeUnmount(() => emit('visibility-change', false))
</script>

<template>
  <el-dialog :model-value="visible" :title="t(multiple ? 'gameTypeChoice.multipleTitle' : 'gameTypeChoice.title')"
    class="game-type-choice-dialog" width="30rem" align-center append-to-body destroy-on-close
    :close-on-click-modal="false" :close-on-press-escape="!live.isChoosingGameType" :show-close="!live.isChoosingGameType"
    @close="live.dismissGameTypeChoice" @closed="() => { if (!visible) emit('visibility-change', false) }">
    <p class="choice-hint">{{ t(multiple ? 'gameTypeChoice.multipleHint' : 'gameTypeChoice.hint') }}</p>
    <fieldset v-if="!multiple" class="game-versions" :disabled="live.isChoosingGameType">
      <legend>{{ t('gameTypeChoice.label') }}</legend>
      <label v-for="preset in presets" :key="preset.id" class="game-version">
        <input v-model="selected" type="radio" name="game-version" :value="preset.id">
        <span><strong>{{ t(preset.nameKey) }}</strong><small>{{ t(preset.descriptionKey) }}</small></span>
      </label>
    </fieldset>
    <template #footer>
      <div class="choice-actions">
        <el-button :disabled="live.isChoosingGameType" @click="live.dismissGameTypeChoice">{{ t('gameTypeChoice.close') }}</el-button>
        <el-button :disabled="live.isChoosingGameType" @click="live.retryGameTypeDetection">{{ t('gameTypeChoice.retry') }}</el-button>
        <el-button v-if="multiple" type="primary" @click="live.selectWindow()">{{ t('gameTypeChoice.reselect') }}</el-button>
        <el-button v-else type="primary" :disabled="!selected" :loading="live.isChoosingGameType"
          @click="selected && live.chooseGameType(selected)">{{ t('gameTypeChoice.confirm') }}</el-button>
      </div>
    </template>
  </el-dialog>
</template>

<style scoped>
:global(.game-type-choice-dialog.el-dialog) { width: min(30rem, calc(100vw - 1.5rem)) !important; max-height: calc(100dvh - 1.5rem); overflow-y: auto; border: 1px solid var(--border-strong); border-radius: var(--radius-card); background: var(--surface-raised); box-shadow: var(--shadow-raised); }
.choice-hint { margin: 0 0 1rem; color: var(--muted-strong); line-height: 1.6; }
.game-versions { border: 0; padding: 0; margin: 0; display: grid; gap: .5rem; }
.game-versions legend { margin-bottom: .625rem; font-weight: 600; }
.game-version { display: flex; align-items: center; gap: .75rem; padding: .75rem; border: 1px solid var(--border); border-radius: .625rem; cursor: pointer; min-height: 3.5rem; }
.game-version:has(:checked) { border-color: var(--primary); background: var(--primary-soft); }
.game-version:focus-within { outline: 2px solid var(--el-color-primary); outline-offset: 2px; }
.game-version input { margin: 0; accent-color: var(--el-color-primary); }
.game-version strong, .game-version small { display: block; }
.game-version small { margin-top: .25rem; color: var(--muted-strong); line-height: 1.4; }
.choice-actions { display: flex; gap: .5rem; flex-wrap: wrap; justify-content: flex-end; }
.choice-actions :deep(.el-button) { margin-left: 0; height: auto; min-height: 2.25rem; }
.choice-actions :deep(.el-button > span) { white-space: normal; line-height: 1.25; }
@media (max-width: 35rem) { .choice-actions :deep(.el-button) { flex: 1 1 auto; } }
</style>
